/* iter 241 Lot 4 — Reusable engine selector.
 *
 * Displays :
 *   - Active engine name + a status badge :
 *       "Vierge" / "Analysé" / "Configuré" (derived by the caller).
 *   - Dropdown of the other engines.
 *   - "Nouveau moteur" button that opens a tiny inline prompt.
 *
 * Hidden if multiEnabled=false (feature flag OFF) → legacy fallback. */
import React, { useState } from 'react';
import { useActiveEngine } from '@/context/EngineContext';
import { Sparkles, ChevronDown, Plus, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function EngineSelector({ projectId, statusBadge }) {
  const { engines, activeEngine, activeEngineId, switchEngine, createEngine,
          multiEnabled, loading } = useActiveEngine();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  if (!multiEnabled) return null;
  if (loading || !activeEngine) {
    return (
      <div data-testid="engine-selector-loading"
           className="text-xs text-semantic-muted px-3 py-2">
        Chargement moteurs…
      </div>
    );
  }

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await createEngine(name);
      toast.success(`Moteur « ${name} » créé`);
      setNewName('');
      setCreating(false);
      setOpen(false);
    } catch (e) {
      toast.error("Impossible de créer le moteur");
    }
  };

  return (
    <div className="relative inline-block" data-testid="engine-selector">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="engine-selector-button"
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
        style={{
          background: 'hsl(var(--color-surface-elevated))',
          border: '1px solid hsl(var(--color-border))',
        }}
      >
        <Sparkles className="w-4 h-4" style={{ color: 'hsl(var(--color-primary))' }} />
        <span data-testid="engine-selector-active-name" className="font-medium">
          {activeEngine.name}
        </span>
        {activeEngine.is_primary && (
          <span className="text-[10px] px-1.5 py-0.5 rounded"
                style={{ background: 'hsl(var(--color-primary) / 0.12)',
                          color: 'hsl(var(--color-primary))' }}>
            principal
          </span>
        )}
        {statusBadge && (
          <span data-testid="engine-selector-status-badge"
                className="text-[10px] px-1.5 py-0.5 rounded"
                style={{ background: statusBadge.bg || 'hsl(var(--color-warning-bg))',
                          color: statusBadge.color || 'hsl(var(--color-warning-text))' }}>
            {statusBadge.label}
          </span>
        )}
        <ChevronDown className="w-4 h-4 opacity-60" />
      </button>

      {open && (
        <div data-testid="engine-selector-dropdown"
             className="absolute z-50 mt-1 min-w-[240px] rounded-lg shadow-lg"
             style={{ background: 'hsl(var(--color-surface))',
                       border: '1px solid hsl(var(--color-border))' }}>
          <div className="max-h-64 overflow-y-auto p-1">
            {engines.map((e) => (
              <button
                key={e.id}
                data-testid={`engine-option-${e.id}`}
                onClick={() => { switchEngine(e.id); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded text-left text-sm hover:opacity-80"
                style={{
                  background: e.id === activeEngineId
                    ? 'hsl(var(--color-primary) / 0.1)'
                    : 'transparent',
                }}
              >
                {e.id === activeEngineId && (
                  <CheckCircle className="w-4 h-4"
                                style={{ color: 'hsl(var(--color-primary))' }} />
                )}
                <div className="flex-1">
                  <div className="font-medium">{e.name}</div>
                  <div className="text-xs text-semantic-muted">
                    {e.is_primary ? 'Moteur principal' : 'Moteur secondaire'}
                  </div>
                </div>
              </button>
            ))}
          </div>
          <div className="p-2 border-t"
               style={{ borderColor: 'hsl(var(--color-border))' }}>
            {creating ? (
              <div className="flex gap-1">
                <input
                  autoFocus
                  data-testid="engine-selector-new-name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  placeholder="Nom du moteur"
                  className="flex-1 px-2 py-1 text-sm rounded"
                  style={{ background: 'hsl(var(--color-surface-elevated))',
                            border: '1px solid hsl(var(--color-border))' }}
                />
                <button
                  data-testid="engine-selector-create-confirm"
                  onClick={handleCreate}
                  className="px-2 py-1 text-sm rounded"
                  style={{ background: 'hsl(var(--color-primary))', color: 'white' }}
                >Créer</button>
              </div>
            ) : (
              <button
                data-testid="engine-selector-create"
                onClick={() => setCreating(true)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm hover:opacity-80"
                style={{ background: 'transparent',
                          color: 'hsl(var(--color-primary))' }}
              >
                <Plus className="w-4 h-4" />
                Créer un nouveau moteur
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
